import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  Lock,
  Shield,
  Copy,
  Check,
  MessageCircle,
  Camera,
  Upload,
  FileText,
  X,
  Image
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import { userAPI } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import CarLoader from '../../components/ui/CarLoader';

// Role descriptions
const roleInfo = {
  manager: {
    name: 'Manager',
    icon: '👔',
    description: 'Manages daily operations - cars, bookings, drivers, journeys. Cannot manage users or settings.',
    color: 'border-purple-500 bg-purple-50'
  },
  accountant: {
    name: 'Accountant',
    icon: '📊',
    description: 'Handles finances - views bookings, manages payments, generates reports.',
    color: 'border-green-500 bg-green-50'
  },
  dispatcher: {
    name: 'Dispatcher',
    icon: '📍',
    description: 'Coordinates trips - assigns drivers to bookings, manages journeys.',
    color: 'border-blue-500 bg-blue-50'
  },
  driver: {
    name: 'Driver',
    icon: '🚗',
    description: 'Views assigned trips and updates journey status.',
    color: 'border-orange-500 bg-orange-50'
  },
  staff: {
    name: 'Staff',
    icon: '👤',
    description: 'General staff with customizable permissions.',
    color: 'border-gray-500 bg-gray-50'
  }
};

const StaffForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    role: 'driver'
  });
  const [showPassword, setShowPassword] = useState(false); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdStaff, setCreatedStaff] = useState(null);
  const [copied, setCopied] = useState(false);

  // Photo & Documents state
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentToUpload, setDocumentToUpload] = useState({
    file: null,
    type: 'license',
    name: '',
    expiryDate: ''
  });
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      fetchStaffData();
    }
  }, [id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStaffData = async () => {
    try {
      setFetchingData(true);
      const response = await userAPI.getStaffById(id);
      const staff = response.data.user;
      setFormData({
        name: staff.name,
        email: staff.email || '',
        phone: staff.phone || '',
        dateOfBirth: staff.dateOfBirth ? staff.dateOfBirth.split('T')[0] : '',
        role: staff.role
      });
      // Set existing photo
      if (staff.photo?.url) {
        setPhotoPreview(staff.photo.url);
      }
      // Set existing documents
      if (staff.documents?.length > 0) {
        setDocuments(staff.documents);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setError('Failed to load staff data');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const generatePassword = () => { // eslint-disable-line no-unused-vars
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEditing) {
        await userAPI.updateStaff(id, {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          dateOfBirth: formData.dateOfBirth,
          role: formData.role
        });
        navigate('/staff');
      } else {
        // Validate phone number
        if (!formData.phone) {
          setError('Phone number is required');
          setLoading(false);
          return;
        }

        const response = await userAPI.createStaff(formData);
        setCreatedStaff({
          ...response.data.user,
          password: response.data.generatedPassword, // Get auto-generated password
          loginInfo: response.data.loginInfo
        });
        setSuccess(true);
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      setError(error.response?.data?.message || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = () => {
    if (!createdStaff) return;

    const text = `🔐 Login Credentials

Name: ${createdStaff.name}
Role: ${roleInfo[createdStaff.role]?.name}

Login ID: ${createdStaff.phone}
Password: ${createdStaff.password}

Login URL: ${window.location.origin}/login

You can change your password anytime from Settings.`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWhatsApp = () => {
    if (!createdStaff) return;

    const text = `🔐 Login Credentials

Name: ${createdStaff.name}
Role: ${roleInfo[createdStaff.role]?.name}

Login ID: ${createdStaff.phone}
Password: ${createdStaff.password}

Login URL: ${window.location.origin}/login

You can change your password anytime from Settings.`;

    const whatsappUrl = `https://wa.me/${createdStaff.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Photo handlers
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoUpload = async () => {
    if (!photo || !id) return;

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('photo', photo);
      const response = await userAPI.uploadStaffPhoto(id, formData);
      setPhotoPreview(response.data.photo.url);
      setPhoto(null);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!id) return;

    try {
      setUploadingPhoto(true);
      await userAPI.deleteStaffPhoto(id);
      setPhotoPreview(null);
      setPhoto(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      setError('Failed to delete photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Document handlers
  const handleDocumentSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Document size should be less than 10MB');
        return;
      }
      setDocumentToUpload(prev => ({ ...prev, file }));
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentToUpload.file || !id) return;

    try {
      setUploadingDocument(true);
      const formData = new FormData();
      formData.append('document', documentToUpload.file);
      formData.append('documentType', documentToUpload.type);
      formData.append('documentName', documentToUpload.name || documentToUpload.type);
      if (documentToUpload.expiryDate) {
        formData.append('expiryDate', documentToUpload.expiryDate);
      }

      const response = await userAPI.uploadStaffDocument(id, formData);
      setDocuments(response.data.documents);
      setDocumentToUpload({ file: null, type: 'license', name: '', expiryDate: '' });
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!id) return;

    try {
      const response = await userAPI.deleteStaffDocument(id, documentId);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };

  const documentTypes = [
    { value: 'license', label: 'Driving License' },
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'aadhar', label: 'Aadhar Card' },
    { value: 'other', label: 'Other' }
  ];

  // Success screen after creating staff
  if (success && createdStaff) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Staff Member Created!</h2>
            <p className="text-gray-600 mb-6">
              {createdStaff.name} has been added as {roleInfo[createdStaff.role]?.name}
            </p>

            {/* Credentials Card */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="h-5 w-5 mr-2" />
                Login Credentials
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Login ID (Phone Number)</p>
                  <p className="font-mono text-gray-900 text-lg">{createdStaff.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Password</p>
                  <p className="font-mono text-gray-900 text-lg">{createdStaff.password}</p>
                  <p className="text-xs text-gray-400 mt-1">Format: Name + Birth Year (e.g., Rahul1990)</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Login URL</p>
                  <p className="font-mono text-blue-600">{window.location.origin}/login</p>
                </div>
              </div>
            </div>

            {/* Share Options */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button onClick={copyCredentials} variant="outline">
                {copied ? (
                  <>
                    <Check className="h-5 w-5 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5 mr-2" />
                    Copy Credentials
                  </>
                )}
              </Button>

              {createdStaff.phone && (
                <Button onClick={shareViaWhatsApp} className="bg-green-600 hover:bg-green-700">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Share via WhatsApp
                </Button>
              )}
            </div>

            <p className="text-sm text-gray-500 mb-6">
              Share these credentials with {createdStaff.name} so they can login.
            </p>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setSuccess(false);
                setCreatedStaff(null);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  dateOfBirth: '',
                  role: 'driver'
                });
              }}>
                Add Another
              </Button>
              <Button onClick={() => navigate('/staff')}>
                Go to Team List
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (fetchingData) {
    return (
      <div className="p-6">
        <CarLoader message="Loading staff data..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Edit Team Member' : 'Add Team Member'}
        description={isEditing ? 'Update staff information' : 'Create a new staff account'}
        action={
          <Button variant="outline" onClick={() => navigate('/staff')}>
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Role Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Select Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(roleInfo).map(([role, info]) => (
                <label
                  key={role}
                  className={`flex items-start p-3 md:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.role === role
                      ? info.color + ' border-2'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={formData.role === role}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <span className="text-xl md:text-2xl mr-2 md:mr-3">{info.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{info.name}</p>
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{info.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Profile Photo - Big and Prominent */}
              <div className="flex flex-col items-center lg:items-start">
                <div className="relative">
                  <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 border-4 border-white shadow-xl flex items-center justify-center overflow-hidden">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center">
                        <Camera className="h-16 w-16 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">No Photo</p>
                      </div>
                    )}
                  </div>
                  {isEditing && photoPreview && (
                    <button
                      type="button"
                      onClick={handlePhotoDelete}
                      disabled={uploadingPhoto}
                      className="absolute -top-2 -right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <div className="mt-4 text-center lg:text-left">
                  <input
                    type="file"
                    ref={photoInputRef}
                    onChange={handlePhotoSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => photoInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    {isEditing && photo && (
                      <Button
                        type="button"
                        onClick={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        className="w-full"
                      >
                        {uploadingPhoto ? 'Saving...' : 'Save Photo'}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">JPG, PNG. Max 5MB</p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                  />

                  <Input
                    label="Phone Number (Login ID)"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="9876543210"
                    required
                    disabled={isEditing}
                    helperText={isEditing ? "Phone cannot be changed" : "This will be used for login"}
                  />

                  <Input
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    helperText="Used to generate default password (Name + Birth Year)"
                  />

                  <Input
                    label="Email Address (Optional)"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Password Info (only for new staff) */}
        {!isEditing && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lock className="h-5 w-5 mr-2" />
                Auto-Generated Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Password will be auto-generated:</strong>
                </p>
                <p className="text-lg font-mono mt-2 text-blue-900">
                  {formData.name ? formData.name.split(' ')[0] : 'Name'}{formData.dateOfBirth ? new Date(formData.dateOfBirth).getFullYear() : new Date().getFullYear()}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Format: First Name + Birth Year (e.g., Rahul1990)
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Staff can change their password anytime from Settings.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents Section (only for editing) */}
        {isEditing && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Existing Documents */}
              {documents.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Documents</p>
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div
                        key={doc._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {documentTypes.find(t => t.value === doc.type)?.label || doc.type}
                            </p>
                            {doc.name && doc.name !== doc.type && (
                              <p className="text-xs text-gray-500">{doc.name}</p>
                            )}
                            {doc.expiryDate && (
                              <p className="text-xs text-gray-500">
                                Expires: {new Date(doc.expiryDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-blue-600 hover:text-blue-700"
                          >
                            <Image className="h-4 w-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDocumentDelete(doc._id)}
                            className="p-1 text-red-500 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload New Document */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Upload New Document</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Document Type
                    </label>
                    <select
                      value={documentToUpload.type}
                      onChange={(e) => setDocumentToUpload(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {documentTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Document Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={documentToUpload.name}
                      onChange={(e) => setDocumentToUpload(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., DL-2023"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Expiry Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={documentToUpload.expiryDate}
                      onChange={(e) => setDocumentToUpload(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="file"
                    ref={documentInputRef}
                    onChange={handleDocumentSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => documentInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Select File
                  </Button>
                  {documentToUpload.file && (
                    <>
                      <span className="text-sm text-gray-600 truncate max-w-[200px]">
                        {documentToUpload.file.name}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleDocumentUpload}
                        disabled={uploadingDocument}
                      >
                        {uploadingDocument ? 'Uploading...' : 'Upload'}
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Supported: PDF, JPG, PNG. Max 10MB
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        {/* Error Message - Above Buttons */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 animate-pulse font-medium">
            {error}
          </div>
        )}

        <div className="flex gap-3 sticky bottom-0 bg-gray-50 py-4 -mx-4 px-4 md:-mx-6 md:px-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/staff')}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              'Saving...'
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                {isEditing ? 'Update' : 'Create'} Staff Member
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StaffForm;
